import type { MaybeRefOrGetter } from 'vue';
import type { CollectionField, CollectionFieldSelectionProps, ModelValueBase } from '#layers/data-model/types/collection-field-selection';
import { computed, toValue } from 'vue';
import { useI18n } from 'vue-i18n';
import { ensureArray } from '#layers/base/public';
import { useDataModelDisplayNames } from '#layers/data-model/composables/use-data-model-display-names';
import { getCollectionFields } from '#layers/data-model/utils/schema/collection';

export function useCollectionFieldSelection<IncludePrimitive extends boolean, IncludeRelation extends boolean, Multiple extends boolean>(
	options: MaybeRefOrGetter<CollectionFieldSelectionProps<IncludePrimitive, IncludeRelation, Multiple>>,
	modelValue: MaybeRefOrGetter<ModelValueBase<IncludePrimitive, IncludeRelation, Multiple>>,
) {
	const { t } = useI18n();
	const { getFieldDisplayName } = useDataModelDisplayNames();
	const fields = computed(() => getCollectionFields(toValue(options).collection, {
		includePrimitiveFields: toValue(options).includePrimitiveFields ?? true,
		includeRelationFields: toValue(options).includeRelationFields ?? true,
		includeConstrainedFields: toValue(options).includeConstrainedFields,
		includeUnsupportedFields: toValue(options).includeUnsupportedFields ?? false,
	})
		.filter((field) => toValue(options).filter?.(field as unknown as CollectionField<IncludePrimitive, IncludeRelation>) ?? true));

	const fieldItems = computed(() => fields.value.map((field) => ({
		id: field.id,
		apiName: field.apiName,
		label: getFieldDisplayName(field),
	})).toSorted((a, b) => a.label.localeCompare(b.label)));

	const unknownFieldItems = computed(() => ensureArray(toValue(modelValue))
		?.filter((id) => id && !fieldItems.value.some((item) => item.id === id))
		.map((id) => ({
			id,
			label: t('collection_field_select.unknown_field'),
			icon: 'lucide:triangle-alert',
			ui: { itemLeadingIcon: '!text-warning' },
			class: 'italic',
		}))
		?? []);

	const items = computed(() => [...fieldItems.value, ...unknownFieldItems.value]);

	return { items };
}
